import { forwardRef } from "react"
import { InputHTMLAttributes, TextareaHTMLAttributes } from "react"
import clsx from "clsx"
import ErrorMessage from "./ErrorMessage"

type InputProps = {
  name: string
  label: string
  description?: string
  placeholder?: string
  type?: "text" | "textarea" | "email"
  error?: string
  hideLabel?: boolean
  className?: string
} & (
  | InputHTMLAttributes<HTMLInputElement>
  | TextareaHTMLAttributes<HTMLTextAreaElement>
)

const Input = forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>(
  (
    {
      name,
      label,
      placeholder,
      description,
      type = "text",
      error,
      hideLabel,
      className,
      ...fieldProps
    },
    ref
  ) => {
    const baseClasses =
      "flex-1 block w-full rounded-xl border-0 py-2 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-chessdrop-600"

    return (
      <div className={clsx(className)}>
        <div className={clsx(hideLabel ? "sr-only" : "mb-1")}>
          <label
            htmlFor={name}
            className="block text-sm leading-5 font-medium text-gray-900"
          >
            {label}
          </label>

          {description && (
            <p className="text-sm leading-5 font-medium text-gray-500">
              {description}
            </p>
          )}
        </div>

        {type === "textarea" ? (
          <textarea
            name={name}
            id={name}
            placeholder={placeholder}
            className={baseClasses}
            rows={2}
            ref={ref as React.Ref<HTMLTextAreaElement>}
            {...(fieldProps as TextareaHTMLAttributes<HTMLTextAreaElement>)}
          />
        ) : (
          <input
            name={name}
            type={type}
            id={name}
            placeholder={placeholder}
            className={baseClasses}
            ref={ref as React.Ref<HTMLInputElement>}
            {...(fieldProps as InputHTMLAttributes<HTMLInputElement>)}
          />
        )}

        {error && <ErrorMessage className="mt-1.5" message={error} />}
      </div>
    )
  }
)

Input.displayName = "Input"

export default Input
